import React, { useState, useEffect, useMemo } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { getActiveOrgId } from '../../utils/orgUtils'
import { buildCloudRequestUrl } from '../../components/integrations/gcpDiscoverySession.mjs'
import { Card, CardContent, CardHeader } from '../../components/ui'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableErrorRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { API_ENDPOINTS } from '../../config/api'
import { Plus, Download, Edit, Trash2, Upload, X, FileText, AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts'
import { PageLayout } from '../../layouts/PageLayout'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { GovernanceInlineFormPanel } from '../../components/governance/GovernanceInlineFormPanel'
import { featureFlags } from '../../config/featureFlags'
import { toEvidenceDisplayId } from '../../utils/evidenceDisplayId'
import {
  EvidenceArtifactCategoryTabs,
  type ArtifactTabKey,
} from '../../components/evidence/EvidenceArtifactCategoryTabs'
import { EvidenceArtifactCategoryBadge } from '../../components/evidence/EvidenceArtifactCategoryBadge'
import {
  EvidenceCategorySelector,
  type ArtifactCategory,
} from '../../components/evidence/EvidenceCategorySelector'
import GovernanceDocumentRagPanel from '../../components/governance/GovernanceDocumentRagPanel'
import GovernanceDocumentIndexStatusBadge from '../../components/governance/GovernanceDocumentIndexStatusBadge'

const MAX_EVIDENCE_UPLOAD_MB = 50

async function readEvidenceUploadError(
  res: Response,
  t: (key: string, fallback?: string) => string,
  fallback: string,
): Promise<string> {
  if (res.status === 413) {
    return t(
      'governance.evidence.upload.fileTooLarge',
      `File is too large. Maximum upload size is ${MAX_EVIDENCE_UPLOAD_MB} MB.`,
    )
  }
  try {
    const data = await res.json()
    return String(data?.error || data?.message || fallback)
  } catch {
    return fallback
  }
}

interface Evidence {
  id: string
  displayId?: string
  title: string
  description?: string
  relatedPolicyId?: string
  controlCode?: string
  filePath?: string
  createdAt: string
  updatedAt: string
  status?: 'Valid' | 'Expiring soon' | 'Expired' | 'Missing'
  daysSinceUpdate?: number
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'EXPIRED'
  verifiedAt?: string
  lifecycleStatus?: 'uploaded' | 'pending_review' | 'approved' | 'rejected' | 'expired' | 'superseded' | 'requires_update' | 'archived'
  freshnessStatus?: 'fresh' | 'aging' | 'stale' | 'expired'
  linkedRecommendationsCount?: number
  artifactCategory?: string
  evidenceSource?: string
  cloudCheck?: {
    cloudEvidenceId: string
    checkStatus: string
    storageStatus: string
    ruleTitle: string
    resourceName: string
    externalId: string
    evidenceHash: string
    evidenceVersion: number
    downloadable: boolean
    mapping?: { controlKey?: string; standardCode?: string } | null
  }
}

interface StatusSummary {
  total: number
  valid: number
  expiringSoon: number
  expired: number
  missing: number
}

interface Policy {
  id: string
  title: string
  code?: string
}

const GovernanceEvidence: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [evidence, setEvidence] = useState<Evidence[]>([])
  const [policies, setPolicies] = useState<Policy[]>([])
  const [statusSummary, setStatusSummary] = useState<StatusSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null)
  const [ragEvidenceId, setRagEvidenceId] = useState<string | null>(null)
  
  // Filters
  const [policyFilter, setPolicyFilter] = useState<string>('')
  const [controlFilter, setControlFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [artifactTab, setArtifactTab] = useState<ArtifactTabKey>('all')
  const cloudQuery = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
  const [sourceFilter, setSourceFilter] = useState(cloudQuery.get('evidenceSource') || '')
  const [storageFilter, setStorageFilter] = useState('')
  const [ruleFilter, setRuleFilter] = useState(cloudQuery.get('ruleId') || '')
  const [integrationFilter, setIntegrationFilter] = useState('')
  const [resourceFilter, setResourceFilter] = useState(cloudQuery.get('externalId') || '')

  useEffect(() => {
    loadEvidence()
    loadPolicies()
    loadStatusSummary()
  }, [])

  useEffect(() => {
    if (createModalOpen || editModalOpen) return
    loadEvidence()
  }, [policyFilter, controlFilter, statusFilter, searchQuery, fromDate, toDate, artifactTab, sourceFilter, storageFilter, ruleFilter, integrationFilter, resourceFilter, createModalOpen, editModalOpen])

  useEffect(() => {
    if (evidence.length === 0) return
    setRagEvidenceId((prev) => {
      if (prev && evidence.some((item) => item.id === prev)) return prev
      return evidence[0].id
    })
  }, [evidence])

  const loadPolicies = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_POLICIES, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setPolicies(data.policies || [])
      }
    } catch (err) {
      console.error('[GovernanceEvidence] Error loading policies:', err)
    }
  }

  const loadEvidence = async () => {
    const orgAtStart = getActiveOrgId()
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      if (policyFilter) params.append('relatedPolicyId', policyFilter)
      if (controlFilter) params.append('controlCode', controlFilter)
      if (statusFilter) params.append('status', statusFilter)
      if (searchQuery) params.append('search', searchQuery)
      if (fromDate) params.append('fromDate', fromDate)
      if (toDate) params.append('toDate', toDate)
      if (featureFlags.EVIDENCE_ARTIFACT_CATEGORIES_V1 && artifactTab !== 'all') {
        params.append('artifactTab', artifactTab)
      }
      if (sourceFilter) params.append('evidenceSource', sourceFilter)
      if (storageFilter) params.append('storageStatus', storageFilter)
      if (ruleFilter) params.append('ruleId', ruleFilter)
      if (integrationFilter) params.append('integrationId', integrationFilter)
      if (resourceFilter) params.append('externalId', resourceFilter)

      const url = `${API_ENDPOINTS.GOVERNANCE_EVIDENCE}${params.toString() ? `?${params.toString()}` : ''}`
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to load evidence')
      const data = await res.json()
      if (getActiveOrgId() !== orgAtStart) return
      setEvidence(data.evidence || [])
      // Reload status summary after evidence is loaded
      await loadStatusSummary()
    } catch (err: any) {
      setError(err.message || 'Failed to load evidence')
      console.error('[GovernanceEvidence] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadStatusSummary = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE_STATUS_SUMMARY, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setStatusSummary(data.summary || null)
      }
    } catch (err) {
      console.error('[GovernanceEvidence] Error loading status summary:', err)
    }
  }

  const handleDeleteEvidence = async (id: string) => {
    if (!confirm(t('governance.evidence.deleteConfirm', 'Are you sure you want to delete this evidence item?'))) {
      return
    }

    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE_ITEM(id), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete evidence')
      }

      await loadEvidence()
    } catch (err: any) {
      alert(err.message || t('governance.evidence.deleteError', 'Failed to delete evidence'))
    }
  }

  const handleDownloadEvidence = async (id: string) => {
    try {
      const url = API_ENDPOINTS.GOVERNANCE_EVIDENCE_DOWNLOAD(id)
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to download evidence')

      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `evidence-${id}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err: any) {
      alert(err.message || t('governance.evidence.downloadError', 'Failed to download evidence'))
    }
  }

  const handleEvidenceVerification = async (id: string, action: 'verify' | 'reject') => {
    try {
      const endpoint = action === 'verify'
        ? API_ENDPOINTS.GOVERNANCE_EVIDENCE_VERIFY(id)
        : API_ENDPOINTS.GOVERNANCE_EVIDENCE_REJECT(id)
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to update verification status')
      }
      await loadEvidence()
    } catch (err: any) {
      alert(err.message || 'Failed to update verification status')
    }
  }

  const handleEvidenceLifecycle = async (id: string, action: 'request-update' | 'archive') => {
    try {
      const endpoint = action === 'archive'
        ? API_ENDPOINTS.GOVERNANCE_EVIDENCE_ARCHIVE(id)
        : API_ENDPOINTS.GOVERNANCE_EVIDENCE_REQUEST_UPDATE(id)
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to update evidence lifecycle')
      }
      await loadEvidence()
    } catch (err: any) {
      alert(err.message || 'Failed to update evidence lifecycle')
    }
  }

  // Get unique control codes for filter
  const availableControlCodes = useMemo(() => {
    return Array.from(new Set(evidence.map(e => e.controlCode).filter(Boolean))).sort()
  }, [evidence])

  const getEvidenceStatusBadgeClass = (status?: string) => {
    switch (status) {
      case 'Valid':
        return listStyles.badgeEvidenceValid
      case 'Expiring soon':
        return listStyles.badgeEvidenceExpiring
      case 'Expired':
        return listStyles.badgeEvidenceExpired
      case 'Missing':
        return listStyles.badgeEvidenceMissing
      default:
        return listStyles.badgeDefault
    }
  }

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'Valid':
        return <CheckCircle2 className={listStyles.badgeIcon} aria-hidden />
      case 'Expiring soon':
        return <Clock className={listStyles.badgeIcon} aria-hidden />
      case 'Expired':
        return <XCircle className={listStyles.badgeIcon} aria-hidden />
      case 'Missing':
        return <AlertCircle className={listStyles.badgeIcon} aria-hidden />
      default:
        return <AlertCircle className={listStyles.badgeIcon} aria-hidden />
    }
  }

  const getVerificationBadge = (verificationStatus?: string) => {
    if (verificationStatus === 'VERIFIED') return <span className={`${listStyles.badge} bg-emerald-100 text-emerald-700`}>Verified</span>
    if (verificationStatus === 'REJECTED') return <span className={`${listStyles.badge} bg-orange-100 text-orange-700`}>Rejected</span>
    if (verificationStatus === 'EXPIRED') return <span className={`${listStyles.badge} bg-amber-100 text-amber-700`}>Expired</span>
    return <span className={`${listStyles.badge} bg-gray-100 text-gray-700`}>Pending verification</span>
  }

  const getFreshnessBadge = (freshnessStatus?: string) => {
    if (freshnessStatus === 'fresh') return <span className={`${listStyles.badge} bg-emerald-100 text-emerald-700`}>Fresh</span>
    if (freshnessStatus === 'aging') return <span className={`${listStyles.badge} bg-amber-100 text-amber-700`}>Aging</span>
    if (freshnessStatus === 'stale') return <span className={`${listStyles.badge} bg-orange-100 text-orange-700`}>Stale</span>
    if (freshnessStatus === 'expired') return <span className={`${listStyles.badge} bg-red-100 text-red-700`}>Expired</span>
    return null
  }

  // Prepare data for aging chart
  const policyFilterOptions = useMemo(
    () =>
      policies.map((p) => ({
        value: p.id,
        label: `${p.title}${p.code ? ` (${p.code})` : ''}`,
      })),
    [policies],
  )

  const controlFilterOptions = useMemo(
    () => availableControlCodes.map((code) => ({ value: code as string, label: code as string })),
    [availableControlCodes],
  )

  const evidenceStatusFilterOptions = useMemo(
    () => [
      { value: 'Valid', label: t('governance.evidence.status.valid', 'Valid') },
      { value: 'Expiring soon', label: t('governance.evidence.status.expiringSoon', 'Expiring soon') },
      { value: 'Expired', label: t('governance.evidence.status.expired', 'Expired') },
      { value: 'Missing', label: t('governance.evidence.status.missing', 'Missing') },
    ],
    [t],
  )

  const agingChartData = useMemo(() => {
    const buckets = [
      { name: '0-30', min: 0, max: 30, color: '#10B981' },
      { name: '31-90', min: 31, max: 90, color: '#FACC15' },
      { name: '91-180', min: 91, max: 180, color: '#F59E0B' },
      { name: '181-365', min: 181, max: 365, color: '#EF4444' },
      { name: '365+', min: 365, max: Infinity, color: '#DC2626' }
    ]

    return buckets.map(bucket => {
      const count = evidence.filter(e => {
        const days = e.daysSinceUpdate || 0
        return days >= bucket.min && days < bucket.max
      }).length
      return {
        range: bucket.name,
        count,
        color: bucket.color
      }
    })
  }, [evidence])

  const staleEvidenceCount = useMemo(
    () => evidence.filter((item) => item.freshnessStatus === 'stale' || item.freshnessStatus === 'expired').length,
    [evidence],
  )
  const unverifiedEvidenceCount = useMemo(
    () => evidence.filter((item) => item.verificationStatus !== 'VERIFIED').length,
    [evidence],
  )

  return (
    <PageLayout pageScope="governance-evidence-page" title={t('governance.evidence.title', 'Control Evidence Library')}
      actions={
        <Button
          type="button"
          size="small"
          color="primary"
          icon={<Plus className={listStyles.badgeIcon} aria-hidden />}
          onClick={() => {
            setSelectedEvidence(null)
            setCreateModalOpen(true)
          }}
          className="!bg-green-600 hover:!bg-green-700 !border-green-600"
        >
          {t('governance.evidence.create', 'Add Evidence')}
        </Button>
      }
    >
      {statusSummary && (
        <GovernanceListingSection title={t('governance.evidence.statusSummaryTitle', 'Evidence status overview')}>
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid5}`}>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
              <p className={listStyles.metricTileLabel}>{t('governance.evidence.statusSummary.total', 'Total')}</p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{statusSummary?.total ?? 0}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileGreen}`}>
              <p className={`${listStyles.metricTileLabel} ${listStyles.actionsGap}`}>
                <CheckCircle2 className={listStyles.badgeIcon} aria-hidden />
                {t('governance.evidence.statusSummary.valid', 'Valid')}
              </p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textGreen}`}>{statusSummary.valid}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
              <p className={`${listStyles.metricTileLabel} ${listStyles.actionsGap}`}>
                <Clock className={listStyles.badgeIcon} aria-hidden />
                {t('governance.evidence.statusSummary.expiringSoon', 'Expiring Soon')}
              </p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{statusSummary.expiringSoon}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
              <p className={`${listStyles.metricTileLabel} ${listStyles.actionsGap}`}>
                <XCircle className={listStyles.badgeIcon} aria-hidden />
                {t('governance.evidence.statusSummary.expired', 'Expired')}
              </p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{statusSummary.expired}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTilePurple}`}>
              <p className={`${listStyles.metricTileLabel} ${listStyles.actionsGap}`}>
                <AlertCircle className={listStyles.badgeIcon} aria-hidden />
                {t('governance.evidence.statusSummary.missing', 'Missing')}
              </p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textPurple}`}>{statusSummary.missing}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
              <p className={`${listStyles.metricTileLabel} ${listStyles.actionsGap}`}>
                <Clock className={listStyles.badgeIcon} aria-hidden />
                Stale / Expired
              </p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{staleEvidenceCount}</p>
            </div>
            <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
              <p className={`${listStyles.metricTileLabel} ${listStyles.actionsGap}`}>
                <AlertCircle className={listStyles.badgeIcon} aria-hidden />
                Unverified
              </p>
              <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{unverifiedEvidenceCount}</p>
            </div>
          </div>
        </GovernanceListingSection>
      )}

      {evidence.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <h3 className={listStyles.chartCardTitle}>
              {t('governance.evidence.agingChart.title', 'Evidence Aging')}
            </h3>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={agingChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name={t('governance.evidence.agingChart.yAxis', 'Number of Evidence Items')}>
                  {agingChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <GovernanceListingSection
        title={t('governance.evidence.list', 'Evidence Items')}
        description={t(
          'governance.evidence.listSubtitle',
          'Filter by policy, control, status, and date range.',
        )}
        toolbar={
          <div className="flex flex-col gap-3 w-full">
            {featureFlags.EVIDENCE_ARTIFACT_CATEGORIES_V1 ? (
              <EvidenceArtifactCategoryTabs active={artifactTab} onChange={setArtifactTab} />
            ) : null}
          <div className={listStyles.filtersRow}>
            <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
              <SearchField
                label={t('governance.evidence.searchLabel', 'Search')}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('governance.evidence.searchPlaceholder', 'Search evidence...')}
                minWidth="14rem"
              />
            </div>
            <div className={listStyles.toolbarField}>
              <Select<string>
                label={t('governance.evidence.filterPolicy', 'Policy')}
                minWidth="12rem"
                placeholder={t('governance.evidence.filterPolicy', 'All Policies')}
                options={policyFilterOptions}
                value={policyFilter === '' ? null : policyFilter}
                onChange={(v) => setPolicyFilter(v ?? '')}
                disabled={policyFilterOptions.length === 0}
              />
            </div>
            {availableControlCodes.length > 0 && (
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('governance.evidence.filterControl', 'Control')}
                  minWidth="10rem"
                  placeholder={t('governance.evidence.filterControl', 'All Controls')}
                  options={controlFilterOptions}
                  value={controlFilter === '' ? null : controlFilter}
                  onChange={(v) => setControlFilter(v ?? '')}
                />
              </div>
            )}
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel}>{t('gcpEvidence.source', 'Source')}</label>
              <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} className={listStyles.dateInput}>
                <option value="">{t('gcpEvidence.anySource', 'Any source')}</option>
                <option value="GCP_CONFIGURATION">{t('gcpEvidence.googleCloud', 'Google Cloud')}</option>
              </select>
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel}>{t('gcpEvidence.storage', 'Storage')}</label>
              <select value={storageFilter} onChange={(event) => setStorageFilter(event.target.value)} className={listStyles.dateInput}>
                <option value="">{t('gcpEvidence.anyStorage', 'Any storage')}</option>
                <option value="STORED">STORED</option>
                <option value="FAILED">FAILED</option>
                <option value="PENDING">PENDING</option>
              </select>
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel}>{t('gcpEvidence.rule', 'Rule')}</label>
              <input value={ruleFilter} onChange={(event) => setRuleFilter(event.target.value)} className={listStyles.dateInput} />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel}>{t('gcpEvidence.integration', 'Integration')}</label>
              <input value={integrationFilter} onChange={(event) => setIntegrationFilter(event.target.value)} className={listStyles.dateInput} />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel}>{t('gcpEvidence.resource', 'Resource')}</label>
              <input value={resourceFilter} onChange={(event) => setResourceFilter(event.target.value)} className={listStyles.dateInput} />
            </div>
            <div className={listStyles.toolbarField}>
              <Select<string>
                label={t('governance.evidence.table.status', 'Status')}
                minWidth="11rem"
                placeholder={t('governance.evidence.filterStatus', 'All Statuses')}
                options={evidenceStatusFilterOptions}
                value={statusFilter === '' ? null : statusFilter}
                onChange={(v) => setStatusFilter(v ?? '')}
              />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel} htmlFor="governance-evidence-from">
                {t('governance.evidence.fromDate', 'From')}
              </label>
              <input
                id="governance-evidence-from"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={listStyles.dateInput}
              />
            </div>
            <div className={listStyles.toolbarField}>
              <label className={listStyles.fieldLabel} htmlFor="governance-evidence-to">
                {t('governance.evidence.toDate', 'To')}
              </label>
              <input
                id="governance-evidence-to"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={listStyles.dateInput}
              />
            </div>
            {(policyFilter || controlFilter || statusFilter || searchQuery || fromDate || toDate) && (
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  icon={<X className={listStyles.badgeIcon} aria-hidden />}
                  onClick={() => {
                    setPolicyFilter('')
                    setControlFilter('')
                    setStatusFilter('')
                    setSearchQuery('')
                    setFromDate('')
                    setToDate('')
                  }}
                >
                  {t('governance.evidence.clearFilters', 'Clear')}
                </Button>
              </div>
            )}
          </div>
          </div>
        }
      >
        {(createModalOpen || editModalOpen) && (
          <EvidenceModal
            open={createModalOpen || editModalOpen}
            onClose={() => {
              setCreateModalOpen(false)
              setEditModalOpen(false)
              setSelectedEvidence(null)
            }}
            evidence={editModalOpen ? selectedEvidence : null}
            policies={policies}
            onSuccess={loadEvidence}
          />
        )}
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                {featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 ? (
                  <th className={listStyles.th} scope="col">
                    {t('governance.evidence.table.evidenceId', 'Evidence ID')}
                  </th>
                ) : null}
                <th className={listStyles.th} scope="col">
                  {t('governance.evidence.table.title', 'Title')}
                </th>
                {featureFlags.EVIDENCE_ARTIFACT_CATEGORIES_V1 ? (
                  <th className={listStyles.th} scope="col">
                    {t('governance.evidenceArtifacts.artifactCategory', 'Artifact category')}
                  </th>
                ) : null}
                <th className={listStyles.th} scope="col">
                  {t('governance.evidence.table.controlCode', 'Control Code')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.evidence.table.policy', 'Related Policy')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.evidence.table.status', 'Status')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.evidence.table.updatedAt', 'Updated')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.evidence.table.file', 'File')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.evidence.table.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <TableLoadingRow colSpan={featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 ? 8 : 7}>
                  {t('common.loading', 'Loading...')}
                </TableLoadingRow>
              )}
              {!loading && error && (
                <TableErrorRow colSpan={featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 ? 8 : 7}>{error}</TableErrorRow>
              )}
              {!loading && !error && evidence.length === 0 && (
                <TableEmptyRow colSpan={featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 ? 8 : 7}>
                  {t('governance.evidence.noEvidence', 'No evidence items found. Add your first evidence item to get started.')}
                </TableEmptyRow>
              )}
              {!loading &&
                !error &&
                evidence.map((item) => {
                  const relatedPolicy = policies.find((p) => p.id === item.relatedPolicyId)
                  const displayId = item.displayId || toEvidenceDisplayId(item.id)
                  return (
                    <tr
                      key={item.id}
                      className={`${listStyles.row} ${ragEvidenceId === item.id ? listStyles.evidenceRowSelected : ''}`}
                      onClick={() => setRagEvidenceId(item.id)}
                    >
                      {featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 ? (
                        <td className={listStyles.td}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-semibold text-slate-800" title={item.id}>
                              {displayId}
                            </span>
                            <button
                              type="button"
                              className="text-[10px] text-sidebarActive hover:underline"
                              onClick={() => void navigator.clipboard.writeText(displayId)}
                            >
                              {t('governance.evidence.copyId', 'Copy ID')}
                            </button>
                          </div>
                        </td>
                      ) : null}
                      <td className={listStyles.td}>
                        <div className={listStyles.cellStrong}>{item.title}</div>
                        {item.description && (
                          <div className={`${listStyles.cellMuted} ${listStyles.lineClamp2}`}>{item.description}</div>
                        )}
                      </td>
                      {featureFlags.EVIDENCE_ARTIFACT_CATEGORIES_V1 ? (
                        <td className={listStyles.td}>
                          <EvidenceArtifactCategoryBadge category={item.artifactCategory} />
                        </td>
                      ) : null}
                      <td className={listStyles.td}>
                        {item.controlCode ? (
                          <span className={`${listStyles.badge} ${listStyles.badgeInProgress}`}>{item.controlCode}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={listStyles.td}>
                        {relatedPolicy ? (
                          <div>
                            <div className={listStyles.cellStrong}>{relatedPolicy.title}</div>
                            {relatedPolicy.code && <div className={listStyles.cellMuted}>{relatedPolicy.code}</div>}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={listStyles.td}>
                        {item.status && (
                          <div className={listStyles.actionsGap}>
                            <span className={`${listStyles.badge} ${getEvidenceStatusBadgeClass(item.status)}`}>
                              {getStatusIcon(item.status)}
                              {item.status === 'Valid' && t('governance.evidence.status.valid', 'Valid')}
                              {item.status === 'Expiring soon' && t('governance.evidence.status.expiringSoon', 'Expiring soon')}
                              {item.status === 'Expired' && t('governance.evidence.status.expired', 'Expired')}
                              {item.status === 'Missing' && t('governance.evidence.status.missing', 'Missing')}
                            </span>
                            {item.cloudCheck ? (
                              <div>
                                <div className={listStyles.cellMuted}>{t('gcpEvidence.checkResult', 'Check result')}</div>
                                <span className={`${listStyles.badge} bg-slate-100 text-slate-700`}>{item.cloudCheck.checkStatus}</span>
                              </div>
                            ) : null}
                            {item.cloudCheck ? (
                              <div>
                                <div className={listStyles.cellMuted}>{t('gcpEvidence.storage', 'Storage')}</div>
                                <span className={`${listStyles.badge} ${item.cloudCheck.storageStatus === 'STORED' ? 'bg-emerald-100 text-emerald-700' : item.cloudCheck.storageStatus === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>{item.cloudCheck.storageStatus}</span>
                              </div>
                            ) : null}
                            {item.cloudCheck ? (
                              <div>
                                <div className={listStyles.cellMuted}>{t('gcpEvidence.verification', 'Verification')}</div>
                                {getVerificationBadge(item.verificationStatus)}
                              </div>
                            ) : getVerificationBadge(item.verificationStatus)}
                            {getFreshnessBadge(item.freshnessStatus)}
                            {item.filePath ? (
                              <div>
                                <div className={listStyles.cellMuted}>{t('gcpEvidence.index', 'Index')}</div>
                                <GovernanceDocumentIndexStatusBadge
                                  sourceType="EVIDENCE"
                                  sourceEntityType="governance_evidence"
                                  sourceEntityId={item.id}
                                />
                              </div>
                            ) : null}
                            {item.linkedRecommendationsCount ? (
                              <span className={`${listStyles.badge} bg-sky-100 text-sky-700`}>
                                Recos: {item.linkedRecommendationsCount}
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className={listStyles.td}>
                        <div>{new Date(item.updatedAt).toLocaleDateString()}</div>
                        {item.daysSinceUpdate !== undefined && (
                          <div className={listStyles.cellMuted}>
                            {item.daysSinceUpdate} {item.daysSinceUpdate === 1 ? 'day' : 'days'} ago
                          </div>
                        )}
                      </td>
                      <td className={listStyles.td}>
                        {item.filePath ? (
                          <div className={`${listStyles.actionsGap} ${listStyles.textGreen}`}>
                            <FileText className={listStyles.badgeIcon} aria-hidden />
                            <span className={listStyles.cellMuted}>{t('governance.evidence.hasFile', 'File attached')}</span>
                          </div>
                        ) : (
                          <span className={listStyles.cellMuted}>{t('governance.evidence.noFile', 'No file')}</span>
                        )}
                      </td>
                      <td className={listStyles.td}>
                        <div className={listStyles.actionsGap}>
                          {(item.cloudCheck ? item.cloudCheck.downloadable : Boolean(item.filePath)) && (
                            <Button
                              type="button"
                              size="small"
                              color="outline"
                              iconOnly
                              icon={<Download className={listStyles.badgeIcon} aria-hidden />}
                              onClick={() => {
                                if (!item.cloudCheck) {
                                  handleDownloadEvidence(item.id)
                                  return
                                }
                                const orgId = getActiveOrgId()
                                if (!token || !orgId || !item.cloudCheck.downloadable) return
                                void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', `/check-evidence/${item.cloudCheck.cloudEvidenceId}/download`, orgId), {
                                  headers: { Authorization: `Bearer ${token}` },
                                }).then(async (response) => {
                                  if (!response.ok || getActiveOrgId() !== orgId) return
                                  const blob = await response.blob()
                                  const href = URL.createObjectURL(blob)
                                  const anchor = document.createElement('a')
                                  anchor.href = href
                                  anchor.download = `${item.cloudCheck?.cloudEvidenceId}.json`
                                  anchor.click()
                                  URL.revokeObjectURL(href)
                                })
                              }}
                              className="border-0 bg-transparent p-1 shadow-none text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title={t('governance.evidence.download', 'Download file')}
                            > </Button>
                          )}
                          {item.cloudCheck && !item.cloudCheck.downloadable ? (
                            <Button
                              type="button"
                              size="small"
                              color="outline"
                              onClick={() => {
                                const orgId = getActiveOrgId()
                                if (!token || !orgId) return
                                void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', `/check-evidence/${item.cloudCheck?.cloudEvidenceId}/retry`, orgId), {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token}` },
                                }).then(() => loadEvidence())
                              }}
                            >
                              {t('gcpEvidence.retry', 'Retry storage')}
                            </Button>
                          ) : null}
                          {!item.filePath && !item.cloudCheck && (
                            <Button
                              type="button"
                              size="small"
                              color="outline"
                              iconOnly
                              icon={<Upload className={listStyles.badgeIcon} aria-hidden />}
                              onClick={() => {
                                setSelectedEvidence(item)
                                setUploadModalOpen(true)
                              }}
                              className="border-0 bg-transparent p-1 shadow-none text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                              title={t('governance.evidence.uploadFile', 'Upload file')}
                            > </Button>
                          )}
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            iconOnly
                            icon={<Edit className={listStyles.badgeIcon} aria-hidden />}
                            onClick={() => {
                              setSelectedEvidence(item)
                              setEditModalOpen(true)
                            }}
                            className="border-0 bg-transparent p-1 shadow-none text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded"
                            title={t('governance.evidence.edit', 'Edit')}
                          > </Button>
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            iconOnly
                            icon={<Trash2 className={listStyles.badgeIcon} aria-hidden />}
                            onClick={() => handleDeleteEvidence(item.id)}
                            className="border-0 bg-transparent p-1 shadow-none text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            title={t('governance.evidence.delete', 'Delete')}
                          > </Button>
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            iconOnly
                            icon={<CheckCircle2 className={listStyles.badgeIcon} aria-hidden />}
                            onClick={() => handleEvidenceVerification(item.id, 'verify')}
                            className="border-0 bg-transparent p-1 shadow-none text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded"
                            title="Mark as verified"
                          > </Button>
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            iconOnly
                            icon={<Clock className={listStyles.badgeIcon} aria-hidden />}
                            onClick={() => handleEvidenceLifecycle(item.id, 'request-update')}
                            className="border-0 bg-transparent p-1 shadow-none text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded"
                            title="Request update"
                          > </Button>
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            iconOnly
                            icon={<X className={listStyles.badgeIcon} aria-hidden />}
                            onClick={() => handleEvidenceVerification(item.id, 'reject')}
                            className="border-0 bg-transparent p-1 shadow-none text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded"
                            title="Reject evidence"
                          > </Button>
                          <Button
                            type="button"
                            size="small"
                            color="outline"
                            iconOnly
                            icon={<FileText className={listStyles.badgeIcon} aria-hidden />}
                            onClick={() => handleEvidenceLifecycle(item.id, 'archive')}
                            className="border-0 bg-transparent p-1 shadow-none text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-900/20 rounded"
                            title="Archive evidence"
                          > </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection
        title={t('governance.evidence.documentAnalysis', 'Document analysis (RAG)')}
        description={t(
          'governance.evidence.ragHint',
          'Click a row above to change the evidence context, or use the panel below for the selected item.',
        )}
      >
        {evidence.find((item) => item.id === ragEvidenceId)?.cloudCheck ? (
          <p className={listStyles.cellMuted}>
            {evidence.find((item) => item.id === ragEvidenceId)?.cloudCheck?.ruleTitle}
            {' · '}
            {evidence.find((item) => item.id === ragEvidenceId)?.cloudCheck?.resourceName}
            {' · '}
            {evidence.find((item) => item.id === ragEvidenceId)?.cloudCheck?.evidenceHash}
            {' · v'}
            {evidence.find((item) => item.id === ragEvidenceId)?.cloudCheck?.evidenceVersion}
            {' · '}
            {evidence.find((item) => item.id === ragEvidenceId)?.cloudCheck?.mapping?.controlKey || t('gcpEvidence.unmapped', 'No confirmed control link')}
          </p>
        ) : null}
        {ragEvidenceId ? (
          <GovernanceDocumentRagPanel
            title={t('governance.evidence.analyzeEvidenceSufficiency', 'Analyze evidence sufficiency')}
            intent="evidence_gap_analysis"
            evidenceIds={[ragEvidenceId]}
            presetQueries={[
              {
                label: t('governance.evidence.sufficiency', 'Evidence sufficiency'),
                query: 'Is the linked evidence sufficient for the related control? Cite document excerpts only.',
              },
              {
                label: t('governance.evidence.missingSupport', 'Missing support'),
                query: 'What supporting evidence appears missing? Use citations only.',
              },
            ]}
          />
        ) : (
          <p className={listStyles.cellMuted}>
            {t('governance.evidence.selectRowForRag', 'Select an evidence row to run citation-grounded document analysis.')}
          </p>
        )}
      </GovernanceListingSection>

      {/* Upload File Modal */}
      {uploadModalOpen && selectedEvidence && (
        <UploadEvidenceFileModal
          open={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false)
            setSelectedEvidence(null)
          }}
          evidenceId={selectedEvidence.id}
          evidenceTitle={selectedEvidence.title}
          onSuccess={loadEvidence}
        />
      )}
    </PageLayout>
  )
}

// Evidence Create/Edit Modal Component
interface EvidenceModalProps {
  open: boolean
  onClose: () => void
  evidence: Evidence | null
  policies: Policy[]
  onSuccess: () => void
}

const EvidenceModal: React.FC<EvidenceModalProps> = ({ open, onClose, evidence, policies, onSuccess }) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const policyFormOptions = useMemo(
    () => [
      { value: '', label: t('governance.evidence.form.noPolicy', 'None') },
      ...policies.map((p) => ({
        value: p.id,
        label: `${p.title}${p.code ? ` (${p.code})` : ''}`,
      })),
    ],
    [policies, t],
  )
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: evidence?.title || '',
    description: evidence?.description || '',
    relatedPolicyId: evidence?.relatedPolicyId || '',
    controlCode: evidence?.controlCode || '',
    file: null as File | null,
    artifactCategory: (evidence?.artifactCategory as ArtifactCategory) || 'OTHER',
  })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setFormData({
        title: evidence?.title || '',
        description: evidence?.description || '',
        relatedPolicyId: evidence?.relatedPolicyId || '',
        controlCode: evidence?.controlCode || '',
        file: null,
        artifactCategory: (evidence?.artifactCategory as ArtifactCategory) || 'OTHER',
      })
      setError(null)
    }
  }, [open, evidence])

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) {
      setError(t('governance.evidence.form.titleRequired', 'Title is required'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      if (evidence) {
        // Update existing
        const res = await fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE_ITEM(evidence.id), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: formData.title,
            description: formData.description || undefined,
            relatedPolicyId: formData.relatedPolicyId || undefined,
            controlCode: formData.controlCode || undefined,
            ...(featureFlags.EVIDENCE_ARTIFACT_CATEGORIES_V1
              ? { artifactCategory: formData.artifactCategory }
              : {}),
          })
        })

        if (!res.ok) {
          const data = await res.json()
          throw new Error(data.error || 'Failed to update evidence')
        }
      } else {
        // Create new
        const formDataToSend = new FormData()
        formDataToSend.append('title', formData.title)
        if (formData.description) formDataToSend.append('description', formData.description)
        if (formData.relatedPolicyId) formDataToSend.append('relatedPolicyId', formData.relatedPolicyId)
        if (formData.controlCode) formDataToSend.append('controlCode', formData.controlCode)
        if (formData.file) formDataToSend.append('file', formData.file)
        // Always send selected category when the selector is shown (avoids server-side inference overriding OTHER).
        if (featureFlags.EVIDENCE_ARTIFACT_CATEGORIES_V1) {
          formDataToSend.append('artifactCategory', formData.artifactCategory || 'OTHER')
        }

        const res = await fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        })

        if (!res.ok) {
          throw new Error(
            await readEvidenceUploadError(res, t, t('governance.evidence.saveError', 'Failed to save evidence')),
          )
        }
      }

      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message || t('governance.evidence.saveError', 'Failed to save evidence'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <GovernanceInlineFormPanel
      title={
        evidence
          ? t('governance.evidence.editTitle', 'Edit Evidence')
          : t('governance.evidence.createTitle', 'Add Evidence Item')
      }
      onClose={onClose}
      footer={
        <>
          <Button type="button" color="outline" size="small" onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="submit" form="governance-evidence-inline-form" color="primary" size="small" disabled={loading}>
            {loading
              ? t('common.saving', 'Saving…')
              : evidence
                ? t('common.save', 'Save')
                : t('common.create', 'Create')}
          </Button>
        </>
      }
    >
      <form id="governance-evidence-inline-form" onSubmit={handleSubmit}>
        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel} htmlFor="evidence-form-title">
            {t('governance.evidence.form.title', 'Title')} *
          </label>
          <input
            id="evidence-form-title"
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className={listStyles.formInput}
          />
        </div>

        <div className={listStyles.formField}>
          <label className={listStyles.formFieldLabel} htmlFor="evidence-form-description">
            {t('governance.evidence.form.description', 'Description')}
          </label>
          <textarea
            id="evidence-form-description"
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            rows={3}
            className={listStyles.formTextarea}
          />
        </div>

        <div className={listStyles.formGrid2}>
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel}>
              {t('governance.evidence.form.relatedPolicy', 'Related Policy')}
            </label>
            <Select<string>
              className="w-full"
              minWidth="100%"
              options={policyFormOptions}
              value={formData.relatedPolicyId}
              onChange={(v) => setFormData((prev) => ({ ...prev, relatedPolicyId: v ?? '' }))}
            />
          </div>

          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel} htmlFor="evidence-form-control">
              {t('governance.evidence.form.controlCode', 'Control Code')}
            </label>
            <input
              id="evidence-form-control"
              type="text"
              value={formData.controlCode}
              onChange={(e) => setFormData((prev) => ({ ...prev, controlCode: e.target.value }))}
              placeholder="AC-01, A.9.2.1"
              className={listStyles.formInput}
            />
          </div>
        </div>

        {featureFlags.EVIDENCE_ARTIFACT_CATEGORIES_V1 ? (
          <EvidenceCategorySelector
            key={evidence?.id ?? 'create'}
            required
            value={formData.artifactCategory}
            uploadFileName={formData.file?.name}
            onChange={(artifactCategory) =>
              setFormData((prev) => ({ ...prev, artifactCategory }))
            }
          />
        ) : null}

        {!evidence ? (
          <div className={listStyles.formField}>
            <label className={listStyles.formFieldLabel} htmlFor="evidence-form-file">
              {t('governance.evidence.form.file', 'File')}
            </label>
            <input
              id="evidence-form-file"
              type="file"
              accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,.png,.jpg,.jpeg,.csv,.json"
              onChange={(e) => {
                const file = e.target.files?.[0] || null
                setFormData((prev) => ({ ...prev, file }))
              }}
              className={listStyles.formInput}
            />
            <p className={listStyles.formFieldHint}>
              {t('governance.evidence.form.fileHint', 'Optional: Upload file now or add it later')}
            </p>
          </div>
        ) : null}

        {error ? <div className={listStyles.formError}>{error}</div> : null}
      </form>
    </GovernanceInlineFormPanel>
  )
}

// Upload Evidence File Modal Component
interface UploadEvidenceFileModalProps {
  open: boolean
  onClose: () => void
  evidenceId: string
  evidenceTitle: string
  onSuccess: () => void
}

const UploadEvidenceFileModal: React.FC<UploadEvidenceFileModalProps> = ({
  open,
  onClose,
  evidenceId,
  evidenceTitle,
  onSuccess
}) => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [loading, setLoading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) {
      setError(t('governance.evidence.upload.fileRequired', 'File is required'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      const formData = new FormData()
      formData.append('file', file)

      const res = await fetch(API_ENDPOINTS.GOVERNANCE_EVIDENCE_UPLOAD(evidenceId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (!res.ok) {
        throw new Error(
          await readEvidenceUploadError(
            res,
            t,
            t('governance.evidence.upload.error', 'Failed to upload file'),
          ),
        )
      }

      setFile(null)
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.message || t('governance.evidence.upload.error', 'Failed to upload file'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative w-full max-w-lg bg-white dark:bg-neutral-900 rounded-xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {t('governance.evidence.upload.title', 'Upload Evidence File')}
          </h3>
          <Button
            type="button"
            size="small"
            color="outline"
            iconOnly
            icon={<X className="w-5 h-5" />}
            onClick={onClose}
            disabled={loading}
            className="border-0 bg-transparent p-1 shadow-none rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            title={t('common.close', 'Close')}
          > </Button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('governance.evidence.upload.evidence', 'Evidence')}: <strong>{evidenceTitle}</strong>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.evidence.upload.file', 'File')} <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              required
              accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,.png,.jpg,.jpeg,.csv,.json"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null)
                setError(null)
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('governance.evidence.upload.fileTypes', 'Supported: PDF, Office docs, images, CSV/XLSX/JSON (max 50MB)')}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" color="outline" size="small" onClick={onClose} disabled={loading}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              size="small"
              disabled={loading || !file}
              icon={<Upload className="w-4 h-4" />}
            >
              {loading ? t('common.uploading', 'Uploading...') : t('governance.evidence.upload.upload', 'Upload File')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default GovernanceEvidence
